import { Link, type Href } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, View } from "react-native";

import { Text } from "@/src/components/ui/BrandText";
import { Alert } from "@/src/lib/alert";
import { friendlyFirebaseError } from "@/src/lib/friendlyFirebaseError";
import { ControlAccessGate } from "@/src/control/ControlAccessGate";
import { useControlAccessContext } from "@/src/control/ControlAccessContext";
import { MonetizationCampaignForm } from "@/src/control/MonetizationCampaignForm";
import {
  createSponsoredCampaign,
  listSponsoredCampaigns,
  type SponsoredCampaignAdmin,
  type SponsoredCampaignInput,
  type SponsoredCampaignStatus,
} from "@/src/control/api";
import { hasControlPermission } from "@/src/control/permissions";
import { campaignDeliveryStateLabel, campaignModerationStatusLabel, campaignStatusLabel, formatControlDate, formatSubjectList, formatTrackList } from "@/src/control/presentation";
import { contentMaxWidths, contentPage } from "@/src/theme/layout";
import { action, border, radius, spacing, surface, text } from "@/src/theme/tokens";

const statuses: { value: SponsoredCampaignStatus | "all"; label: string }[] = [
  { value: "all", label: "Toutes" },
  { value: "draft", label: "Brouillons" },
  { value: "active", label: "Actives" },
  { value: "paused", label: "En pause" },
  { value: "ended", label: "Terminées" },
];

export default function MonetizationCenterScreen() {
  return <ControlAccessGate permission="monetization.read"><MonetizationCenter /></ControlAccessGate>;
}

function MonetizationCenter() {
  const access = useControlAccessContext();
  const canManage = access.status === "allowed" && hasControlPermission(access.member, "monetization.manage");
  const [status, setStatus] = useState<SponsoredCampaignStatus | "all">("all");
  const [items, setItems] = useState<SponsoredCampaignAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const refresh = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    listSponsoredCampaigns(status)
      .then((value) => active && setItems(value))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [attempt, status]);

  async function create(input: SponsoredCampaignInput) {
    if (creating) return;
    setCreating(true);
    try {
      await createSponsoredCampaign(input);
      setShowCreate(false);
      refresh();
      Alert.alert("Campagne créée", "Le brouillon a été créé et envoyé en attente de modération.");
    } catch (cause: unknown) {
      Alert.alert("Création refusée", friendlyFirebaseError(cause, "La campagne n’a pas pu être créée."));
    } finally {
      setCreating(false);
    }
  }

  return (
    <ScrollView contentContainerStyle={styles.page}>
      <View style={styles.headerRow}>
        <View style={styles.headerText}>
          <Text style={styles.kicker}>CAMPAGNES</Text>
          <Text style={styles.title}>Campagnes sponsorisées</Text>
          <Text style={styles.subtitle}>Prépare les campagnes, vérifie leur ciblage et suis leur diffusion dans le fil.</Text>
        </View>
        {canManage ? <Pressable accessibilityRole="button" onPress={() => setShowCreate((value) => !value)} style={styles.primary}><Text style={styles.primaryText}>{showCreate ? "Fermer" : "Nouvelle campagne"}</Text></Pressable> : null}
      </View>

      {showCreate && canManage ? <View style={styles.panel}><Text style={styles.sectionTitle}>Nouvelle campagne</Text><Text style={styles.note}>Toute nouvelle campagne commence en brouillon et doit être validée avant sa diffusion.</Text><MonetizationCampaignForm busy={creating} onSubmit={create} submitLabel="Créer le brouillon" /></View> : null}

      <ScrollView horizontal style={styles.filterRail} contentContainerStyle={styles.filters} showsHorizontalScrollIndicator={false}>
        {statuses.map((item) => <Pressable accessibilityRole="tab" accessibilityState={{ selected: status === item.value }} key={item.value} onPress={() => setStatus(item.value)} style={[styles.filter, status === item.value && styles.filterActive]}><Text style={[styles.filterText, status === item.value && styles.filterTextActive]}>{item.label}</Text></Pressable>)}
      </ScrollView>

      {loading ? <ActivityIndicator color={text.brand} /> : null}
      {error ? <Pressable accessibilityRole="button" onPress={refresh} style={styles.panel}><Text style={styles.sectionTitle}>Chargement impossible</Text><Text style={styles.link}>Réessayer</Text></Pressable> : null}
      {!loading && !error && !items.length ? <View style={styles.panel}><Text style={styles.note}>Aucune campagne dans ce filtre.</Text></View> : null}
      {!loading && !error ? items.map((item) => <CampaignRow key={item.id} item={item} />) : null}
    </ScrollView>
  );
}

function CampaignRow({ item }: { item: SponsoredCampaignAdmin }) {
  const title = item.headline || item.advertiserName || "Campagne sans titre";
  return (
    <Link href={`/control/monetization/${item.id}` as Href} asChild>
      <Pressable accessibilityRole="link" style={styles.row}>
        <View style={styles.rowTop}>
          <View style={styles.rowText}>
            <Text style={styles.rowTitle}>{title}</Text>
            {item.headline ? <Text style={styles.advertiser}>{item.advertiserName}</Text> : null}
          </View>
          <StatusBadge value={item.status} />
        </View>
        <View style={styles.badges}>
          <Badge label={`Validation : ${campaignModerationStatusLabel(item.moderationStatus)}`} />
          <Badge label={`Diffusion : ${campaignDeliveryStateLabel(item.deliveryState)}`} />
        </View>
        <Text numberOfLines={2} style={styles.body}>{item.body}</Text>
        <View style={styles.infoGroup}>
          <Text style={styles.meta}><Text style={styles.metaStrong}>Ciblage :</Text> {formatSubjectList(item.targetSubjects)} · {formatTrackList(item.targetTracks)}</Text>
          <Text style={styles.meta}><Text style={styles.metaStrong}>Fréquence :</Text> {item.frequencyCap.maxImpressions} impression{item.frequencyCap.maxImpressions === 1 ? "" : "s"} / {item.frequencyCap.windowHours} h</Text>
          <Text style={styles.meta}>Début : {formatControlDate(item.startsAt)}</Text>
        </View>
        <Text style={styles.open}>Ouvrir la campagne →</Text>
      </Pressable>
    </Link>
  );
}

function StatusBadge({ value }: { value: SponsoredCampaignStatus }) {
  return <Text style={[styles.status, value === "active" ? styles.statusActive : value === "ended" ? styles.statusEnded : styles.statusOther]}>{campaignStatusLabel(value)}</Text>;
}

function Badge({ label }: { label: string }) {
  return <Text style={styles.badge}>{label}</Text>;
}

const styles = StyleSheet.create({
  page: { ...contentPage(contentMaxWidths.control), gap: spacing.lg },
  headerRow: { alignItems: "flex-start", flexDirection: "row", flexWrap: "wrap", gap: spacing.lg, justifyContent: "space-between" },
  headerText: { flex: 1, gap: spacing.sm, minWidth: 0 },
  kicker: { color: text.brand, fontSize: 12, fontWeight: "800", letterSpacing: 1.5 },
  title: { color: text.primary, fontSize: 30, fontWeight: "800" },
  subtitle: { color: text.secondary, lineHeight: 23, minWidth: 0 },
  filterRail: { flexGrow: 0, maxWidth: "100%", width: "100%" },
  primary: { alignItems: "center", backgroundColor: action.primary, borderRadius: radius.md, minHeight: 48, justifyContent: "center", paddingHorizontal: spacing.lg, paddingVertical: spacing.md },
  primaryText: { color: text.inverse, fontWeight: "800" },
  filters: { gap: spacing.sm },
  filter: { borderColor: border.subtle, borderRadius: radius.pill, borderWidth: 1, minHeight: 44, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  filterActive: { backgroundColor: surface.selected, borderColor: border.brand },
  filterText: { color: text.secondary, fontWeight: "700" },
  filterTextActive: { color: text.brand },
  panel: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.lg, borderWidth: 1, gap: spacing.md, minWidth: 0, padding: spacing.lg },
  sectionTitle: { color: text.primary, fontSize: 18, fontWeight: "800" },
  note: { color: text.secondary, lineHeight: 21 },
  link: { color: text.brand, fontWeight: "700" },
  row: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.lg, borderWidth: 1, gap: spacing.md, minWidth: 0, padding: spacing.lg },
  rowTop: { alignItems: "flex-start", flexDirection: "row", flexWrap: "wrap", gap: spacing.md, justifyContent: "space-between" },
  rowText: { flex: 1, flexShrink: 1, gap: spacing.xs, minWidth: 0 },
  rowTitle: { color: text.primary, fontSize: 17, fontWeight: "800", minWidth: 0 },
  advertiser: { color: text.secondary, minWidth: 0 },
  badges: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
  badge: { backgroundColor: surface.selected, borderRadius: radius.pill, color: text.secondary, fontSize: 11, fontWeight: "700", overflow: "hidden", paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  status: { borderRadius: radius.pill, fontSize: 12, fontWeight: "800", overflow: "hidden", paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  statusActive: { backgroundColor: surface.success, color: text.info },
  statusEnded: { backgroundColor: surface.selected, color: text.muted },
  statusOther: { backgroundColor: surface.warning, color: text.brand },
  body: { color: text.secondary, lineHeight: 21, minWidth: 0 },
  infoGroup: { gap: spacing.xs, minWidth: 0 },
  meta: { color: text.muted, fontSize: 12, lineHeight: 18, minWidth: 0 },
  metaStrong: { color: text.secondary, fontWeight: "800" },
  open: { color: text.brand, fontSize: 12, fontWeight: "800" },
});
